/**
 * Form definitions, adapted into the shapes the app already renders
 * (form-builder wave PR-4; spec `docs/superpowers/specs/2026-08-20-form-builder-design.md`).
 *
 * The wizard, the pre-screen, and the staff surfaces stop importing their
 * questions from `intakeFields.ts` / `eligibilityContent.ts` and start reading a
 * published `FormDefinitionVersion`. Deliberately, the RENDERERS do not change:
 * this module converts a definition field into the exact `FieldSpec` that
 * `IntakeFieldInput` already takes, and a definition screen into the exact
 * `ApplicationScreen` the wizard already walks. The only thing that moves is
 * where the questions come FROM.
 *
 * `id` → `key` is the whole identity mapping, and it is not a coincidence: a
 * definition field's id IS its dotted storage key, so `${section_id}.${id}` is
 * the same answer path the wizard has always written to.
 */
import { get, setPath } from './intakeFields';
import type {
  FieldSpec,
  FieldType,
  SectionKey,
  ApplicationScreen,
  VisibleWhen,
} from './intakeFields';

/** D-FB-11's shape, declared on the renderer contract next to `FieldSpec`.
 *  Re-exported here because this is where definition consumers import from. */
export type { VisibleWhen };

// ── #2109 R2 (lane L0): form translations on the version body (brief B9) ────
// The shapes below are the contract every R2 lane builds on. The helpers that
// read them live in `lib/formI18n.ts` (mirrored by `backend/src/lib/formI18n.js`).
// Options are keyed by VALUE under the field’s block, never on the option
// object, so a reorder or retire never moves a translation onto another answer.

/** A code a translation may be keyed by: every UI language but English. */
export type TranslationLanguage = 'es' | 'pt' | 'fr' | 'zh';
/** The five UI catalog codes a form can be offered in (never `fil`). */
export type FormLanguage = 'en' | TranslationLanguage;
/** Per-string and per-language review state, lowest first (`approved` needs a
 *  named reviewer and is unreachable in R2 — brief B14). */
export type I18nReviewState = 'draft' | 'machine' | 'reviewed' | 'approved';

/** One translated string. `source_hash` is sha256 hex of the English it was
 *  made from; when the live English hashes differently the entry is stale and
 *  readers fall back to English. */
export interface I18nEntry {
  text: string;
  source_hash: string;
  status: I18nReviewState;
  model?: string | null;
  updated_at?: string | null;
}

/** `field.i18n[code]`. */
export interface FieldI18nBlock {
  label?: I18nEntry;
  helper?: I18nEntry;
  placeholder?: I18nEntry;
  groupLabel?: I18nEntry;
  /** Keyed by the driver value, like `conditionalLabel.byValue`. */
  conditionalLabel?: Record<string, I18nEntry>;
  /** Keyed by the option’s immutable `value`. */
  options?: Record<string, { label?: I18nEntry; miss_line?: I18nEntry }>;
  eligible_range?: { miss_line?: I18nEntry };
  date_bound?: { message?: I18nEntry };
  /** driver value → option value → label. */
  conditionalOptions?: Record<string, Record<string, { label?: I18nEntry }>>;
}

/** `section.i18n[code]`. */
export interface SectionI18nBlock {
  title?: I18nEntry;
  intro?: I18nEntry;
}

/** `screen.i18n[code]`. Groups have no id, so their labels key by index; the
 *  hash makes a reordered group fall back to English rather than mislabel. */
export interface ScreenI18nBlock {
  label?: I18nEntry;
  eyebrow?: I18nEntry;
  title?: I18nEntry;
  groups?: Record<string, { label?: I18nEntry }>;
}

export type I18nMap<B> = Partial<Record<TranslationLanguage, B>>;

/** `version.copy_i18n[code]`: a FLAT map of dotted copy path → entry
 *  (`'document_checklist.items.0.label'`). Top-level, not `copy.i18n`, so the
 *  copy-slot registry never sees a translation leaf. */
export type CopyI18n = I18nMap<Record<string, I18nEntry>>;

/** The two language keys on `version.settings` (brief B9). Both are optional;
 *  absent `offered_languages` means English only. `language_review` is derived
 *  by the server on every save and publish (R2-B9). */
export interface FormLanguageSettings {
  offered_languages?: FormLanguage[];
  language_review?: Partial<Record<TranslationLanguage, I18nReviewState>>;
}

/** One answer option as a version stores it. */
export interface DefinitionOption {
  value: string;
  label: string;
  order: number;
  retired?: boolean;
  /** `prescreen` only — how this answer scores against TRF's criteria. */
  eligibility?: 'pass' | 'miss' | 'neutral';
  /** `prescreen` only — the line a family reads when this answer is a miss. */
  miss_line?: string;
  // No `i18n` here on purpose: option translations live on the FIELD’s block,
  // `field.i18n[code].options[value]` (brief B9).
}

/** One question as a version stores it. */
export interface DefinitionField {
  id: string;
  label: string;
  type: FieldType | 'rating';
  section_id: string;
  order: number;
  csv_position: number;
  required?: boolean;
  retired?: boolean;
  /** D-FB-14(a): this question is health information — the builder (PR-5)
   *  refuses to place it on a page before the PHI-consent step. Absent means
   *  false. Renderers ignore it; it is a placement rule, not a display one. */
  phi?: boolean;
  options?: DefinitionOption[];
  helper?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  format?: FieldSpec['format'];
  display?: FieldSpec['display'];
  groupLabel?: string;
  conditionalOptions?: FieldSpec['conditionalOptions'];
  conditionalLabel?: FieldSpec['conditionalLabel'];
  /** D-FB-11: ask this question only when another answer matches (#1639).
   *  Written by the builder, evaluated by `isRevealed` below. It used to live
   *  only on the builder's own `EditorField`, which is why the wizard never saw
   *  it — the property existed on the wire and had nowhere to land. */
  visible_when?: VisibleWhen;
  /** #1838: a date question's own bound. `past` refuses a date after today,
   *  `future` refuses one before it, both compared at end-of-day UTC. The
   *  optional `message` overrides the sentence built from the label; the three
   *  seeded #1111 rules use it to keep their exact wording. */
  date_bound?: { direction: 'past' | 'future'; message?: string };
  /** #1838: this date question compared against ANOTHER date question.
   *  `not_before` refuses an answer earlier than the named field, `not_after`
   *  one later than it. A comparison whose target cannot be resolved is
   *  dropped rather than reported against a question nobody can name. */
  date_compare?: {
    op: 'not_before' | 'not_after';
    section_id: string;
    field_id: string;
    message?: string;
  };
  /** `prescreen` numeric fields — the range that does NOT miss. */
  eligible_range?: { min?: number; max?: number; miss_line: string };
  /** `prescreen` numeric fields — the server accepts an UNRANGED numeric only
   *  when this is explicitly 'neutral' (guardrail c, `assertRenderable`). */
  eligibility?: 'pass' | 'miss' | 'neutral';
  /** #2109 R2-B3: a free-text answer to this question is machine-translated
   *  for staff after submit. Only `text`/`textarea` questions carry it. */
  translate_answer?: boolean;
  /**
   * 2026-09-29 (spec 2026-09-29-committee-one-pager): this question's answer
   * prints in the facts column of the committee one-pager. The server refuses
   * turning it on for `file`/`signature` fields — there is no text to print.
   * Absent means off.
   */
  committee_one_pager?: boolean;
  /**
   * 2026-09-29: a short label for this question on the one-pager
   * ("Household Size" rather than the full question) — 1–40 chars after trim
   * when present. Absent means the question's own label prints instead.
   */
  committee_label?: string;
  /** #2109 brief B9: this question’s translations, per language. */
  i18n?: I18nMap<FieldI18nBlock>;
}

export interface DefinitionSection {
  id: string;
  title: string;
  intro?: string;
  order: number;
  i18n?: I18nMap<SectionI18nBlock>;
}

export interface DefinitionScreen {
  id: string;
  label: string;
  eyebrow: string;
  title: string;
  order: number;
  groups: Array<{ label: string | null; field_ids: Array<{ section_id: string; id: string }> }>;
  i18n?: I18nMap<ScreenI18nBlock>;
}

/** One version row inside the staff list payload — a summary, not a body. */
export interface FormVersionSummary {
  version_n: number;
  status: 'draft' | 'published' | 'superseded';
  change_note?: string | null;
  published_at?: string | null;
  published_by_role?: string | null;
}

/**
 * One definition as GET /api/forms lists it for the builder (PR-5): the
 * definition document plus a compact per-version summary. Deliberately NOT the
 * full body — the templates pane needs identity and lifecycle state, and every
 * quantity it shows must come from this one named payload.
 */
export interface FormDefinitionSummary {
  slug: string;
  kind: 'prescreen' | 'application' | 'checkin' | 'custom';
  title: string;
  status: 'active' | 'archived';
  published_version_id: string | null;
  draft_version_id: string | null;
  versions: FormVersionSummary[];
}

export type FormKind = 'prescreen' | 'application' | 'checkin' | 'custom';

// ── staff builder payloads (PR-5) ───────────────────────────────────────────
// The raw documents `GET /api/forms/:slug` returns — the definition row plus
// every version row, draft included. Distinct from `FormDefinitionVersion`
// below, which is the flattened consumer shape the published routes serve.

/** The `form_definitions` document as the staff routes return it. */
export interface FormDefinitionDoc {
  _id: string;
  slug: string;
  kind: FormKind;
  title: string;
  status: 'active' | 'archived';
  draft_version_id: string | null;
  published_version_id: string | null;
  created_at?: string;
  updated_at?: string;
}

/** One `form_definition_versions` row — a version's full editable body. */
export interface FormVersionDoc {
  _id: string;
  definition_id: string;
  version_n: number;
  status: 'draft' | 'published' | 'superseded';
  sections: DefinitionSection[];
  screens: DefinitionScreen[];
  fields: DefinitionField[];
  settings: Record<string, unknown> & FormLanguageSettings;
  copy: Record<string, any>;
  /** #2109 brief B9: the pre-screen copy slots, translated. */
  copy_i18n?: CopyI18n;
  change_note: string | null;
  /** The account id. On a DRAFT row this is whoever saved it LAST:
   *  `saveDraft` rewrites it on every save (#3108). */
  created_by?: string | null;
  created_by_role?: string | null;
  /** #3108 — the open draft row only (`GET /api/forms/:slug`): the last
   *  saver's name for the builder header. Null when no account saved it. */
  updated_by_name?: string | null;
  published_by_role?: string | null;
  published_at?: string | null;
  created_at?: string;
  /** The CAS token draft saves compare-and-swap on (last-writer-wins, spec §Risks). */
  updated_at: string;
}

/** `GET /api/forms/:slug` — the definition plus every version. This is also
 *  the History panel's data; there is no separate history route. */
export interface FormDetail {
  definition: FormDefinitionDoc;
  versions: FormVersionDoc[];
}

/** An option as `PATCH /:slug/draft` accepts it: a NEW option carries NO
 *  `value` — the server mints one from the label. A client-proposed value the
 *  definition never issued is REJECTED by name (immutability rule 1), so the
 *  builder must omit, not invent. */
export interface DraftOptionInput extends Omit<DefinitionOption, 'value'> {
  value?: string;
  /** #1527 / B-551 — the local placeholder this option carried while unsaved,
   *  present ONLY when `value` is absent. The `local_` prefix is the contract:
   *  the server reads it as a remap key for references that name this answer
   *  (`visible_when.values`) and NEVER stores it, so rule 1 stays literally
   *  true — a client still cannot propose or rename a value. */
  local_value?: string;
}

/** A field as the draft-save route accepts it: a NEW field omits `id` and
 *  `csv_position` — the server mints the id and reserves the column. */
export interface DraftFieldInput extends Omit<DefinitionField, 'id' | 'csv_position' | 'options'> {
  id?: string;
  csv_position?: number;
  options?: DraftOptionInput[];
  /**
   * #1527 / B-551 — the local placeholder (`__new_N`) this field carried while
   * unsaved, present ONLY when `id` is absent.
   *
   * NOT a proposed id, and named so it cannot be mistaken for one. The client
   * places a question the moment staff add it, writing the placeholder into
   * `screens[].groups[].field_ids[]` — but rule 1 forbids proposing an id, so
   * the id is stripped and the reference would otherwise name something no
   * field in the payload claims. The server correlates the two through this
   * key, mints the real id, rewrites every reference, and drops the key. It is
   * never persisted.
   */
  local_id?: string;
}

/** The `PATCH /api/forms/:slug/draft` body. `expected_updated_at` is the CAS
 *  token: include it when a draft row was loaded, omit it (never null — the
 *  validator only tolerates absence) on the first save of a new draft. */
export interface SaveDraftPayload {
  sections: DefinitionSection[];
  screens?: DefinitionScreen[];
  fields: DraftFieldInput[];
  settings?: Record<string, unknown> & FormLanguageSettings;
  copy?: Record<string, any>;
  copy_i18n?: CopyI18n;
  expected_updated_at?: string;
}

// ── retire impact (PR-5 §The consequence layer; D-FB-13) ────────────────────
// The shape `GET /api/forms/:slug/impact/field/:fieldId` returns
// (backend/src/services/formImpact.js). Every value names the query that
// produced it, and `unknown` is a DIFFERENT answer from `0` — the UI must
// never collapse the two (acceptance criterion 1).

/** One counted fact. `state: 'unknown'` means the query failed — render "we
 *  couldn't check", never a zero. */
export type ImpactCountItem =
  | { state: 'ok'; count: number; query: string }
  | { state: 'unknown'; count: null; query: string; error: string };

/** The CSV-column fact — definition data, not a live count. */
export interface ImpactCsvItem {
  state: 'ok';
  value: { csv_position: number | null; retained: boolean };
  query: string;
}

/**
 * The freeze fact (#1106) — definition data, not a live count. Retiring a
 * question on a check-in removes it from FUTURE sends; instances already sent
 * are frozen copies and are not rewritten.
 */
export interface ImpactFutureSendsItem {
  state: 'ok';
  value: { removed_from_future_sends: boolean; sent_instances_frozen: boolean };
  query: string;
}

interface FieldRetireImpactBase {
  slug: string;
  /** Criterion 6: 'caseload' means the server pinned every count to the
   *  caller's own lane; the modal must SAY so. */
  scope: 'org' | 'caseload';
}

/** Answers live in `family_applications`, keyed by `${section_id}.${id}`. */
export interface ApplicationFieldRetireImpact extends FieldRetireImpactBase {
  kind: 'application';
  field: {
    id: string;
    section_id: string;
    label: string | null;
    flag_key: string;
    live_in_published: boolean;
    retired: boolean;
  };
  items: {
    families_mid_draft: ImpactCountItem;
    flagged_needs_more_info: ImpactCountItem;
    csv_column: ImpactCsvItem;
    submitted_stamped: ImpactCountItem;
  };
}

/**
 * Answers live in the poll pipeline (#1106). These counts are HISTORICAL —
 * what the sent instances already asked and families already answered, all of
 * which stays readable — NOT a warning about what breaks. A surface that
 * renders them as a consequence of proceeding states something the freeze
 * rules out.
 */
export interface CheckinFieldRetireImpact extends FieldRetireImpactBase {
  kind: 'checkin' | 'custom';
  field: {
    id: string;
    section_id: string;
    label: string | null;
    /** All a section leaves on a sent instance is this display string. */
    section_label: string | null;
    live_in_published: boolean;
    retired: boolean;
  };
  items: {
    sends_including_question: ImpactCountItem;
    open_sends_still_asking: ImpactCountItem;
    responses_recorded: ImpactCountItem;
    future_sends: ImpactFutureSendsItem;
  };
}

/** Discriminated on `kind`: one impact source per kind, never a merged shape
 *  where a consumer can read a field the server did not send. `prescreen` is
 *  absent by construction — those numbers do not exist. */
export type FieldRetireImpact = ApplicationFieldRetireImpact | CheckinFieldRetireImpact;

/** A published version, exactly as the consumer routes serve it. */
export interface FormDefinitionVersion {
  slug: string;
  kind: 'prescreen' | 'application' | 'checkin' | 'custom';
  title: string;
  definition_id: string;
  version_n: number;
  sections: DefinitionSection[];
  screens: DefinitionScreen[];
  fields: DefinitionField[];
  settings: Record<string, unknown> & FormLanguageSettings;
  copy: Record<string, any>;
  /** #2109 brief B9: the pre-screen copy slots, translated. */
  copy_i18n?: CopyI18n;
}

/**
 * A definition field as a `FieldSpec` — the props `IntakeFieldInput` takes.
 * Optional properties are copied only when present so an adapted spec is
 * indistinguishable from a hand-written one (a stray `min: undefined` would
 * still render a `min` attribute).
 */
export function toFieldSpec(field: DefinitionField): FieldSpec {
  const spec: FieldSpec = {
    key: field.id,
    label: field.label,
    type: field.type as FieldType,
  };
  const liveOptions = (field.options ?? []).filter((o) => !o.retired);
  if (liveOptions.length) spec.options = liveOptions.map((o) => ({ value: o.value, label: o.label }));
  // Display-only lookup over EVERY option, retired included, plus the
  // conditional sets (`residence_state` keeps its US/CA lists there rather than
  // in `options[]`). See the FieldSpec comment: a retired option must not turn
  // a family's stored answer into a slug on the staff surface.
  const conditional = Object.values(field.conditionalOptions?.byValue ?? {}).flat();
  const everyOption = [...(field.options ?? []), ...conditional];
  if (everyOption.length) {
    spec.optionLabels = Object.fromEntries(everyOption.map((o) => [o.value, o.label]));
  }
  if (field.required) spec.required = true;
  if (field.helper !== undefined) spec.helper = field.helper;
  if (field.placeholder !== undefined) spec.placeholder = field.placeholder;
  if (field.min !== undefined) spec.min = field.min;
  if (field.max !== undefined) spec.max = field.max;
  if (field.format !== undefined) spec.format = field.format;
  // #1838: date rules ride through to the FieldSpec so the staff drawer and
  // any other spec-driven renderer see them too. The wizard evaluates them
  // through `dateRules.rulesFromFields` against the DEFINITION rather than
  // through the spec, because a comparison needs the other field's label and a
  // single spec cannot carry its neighbour.
  if (field.date_bound !== undefined) spec.dateBound = field.date_bound;
  if (field.date_compare !== undefined) spec.dateCompare = field.date_compare;
  if (field.display !== undefined) spec.display = field.display;
  if (field.groupLabel !== undefined) spec.groupLabel = field.groupLabel;
  if (field.conditionalOptions) spec.conditionalOptions = field.conditionalOptions;
  if (field.conditionalLabel) spec.conditionalLabel = field.conditionalLabel;
  if (field.visible_when) spec.visibleWhen = field.visible_when;
  return spec;
}

// ── D-FB-11: is this question asked at all? (#1639) ─────────────────────────
//
// ONE interpretation, two renderers. The builder's preview promises a family
// will not be asked a hidden question, and the wizard is what actually asks
// them; before #1639 the preview evaluated the rule and the wizard did not
// carry the property, so the two disagreed by construction. Both now call
// `isRevealed` — a second implementation is how this class of bug recurs.
//
// The semantics are the backend's, deliberately: `backend/src/lib/eligibility.js`
// `isRevealed` (the pre-screen store gate, the scorer) and
// `backend/src/services/publishedForms.js` `missingRequiredFields` (the submit
// gate) already settled them, and the client now has to agree with the server
// about the same submitted application. That includes the FAIL-OPEN posture: a
// dangling or retired driver reveals the question rather than silently dropping
// it, because a misconfigured condition belongs to the builder to flag, not to
// a gate to resolve by hiding a question TRF meant to ask.

/** What a driver lookup needs to know about a question. `EditorField`,
 *  `DefinitionField` and the builder's own by-id map all satisfy it. */
export interface DriverField {
  id: string;
  section_id: string;
  retired?: boolean;
}

/** Fields by id — `visible_when.field_id` names a question, not a path. */
export type DriverIndex = ReadonlyMap<string, DriverField>;

/** Answers as the wizard and the preview hold them: one object per storage
 *  section, each nested exactly as the application document stores it. */
export type SectionAnswers = Readonly<Record<string, Record<string, unknown> | undefined>>;

/** Index a version's fields by id, for driver lookups. Retired fields are kept
 *  — the rule needs to SEE a retired driver to fail open on it. */
export function driverIndex(fields: readonly DriverField[] | null | undefined): DriverIndex {
  return new Map((fields ?? []).map((f) => [f.id, f]));
}

/**
 * Whether a question carrying `condition` is asked right now.
 *
 * Takes the condition rather than the field so the same call serves a
 * `DefinitionField` (`field.visible_when`, the builder preview) and a
 * `FieldSpec` (`spec.visibleWhen`, the wizard) without either side re-deriving
 * anything. No condition means the question is asked of everyone.
 */
export function isRevealed(
  condition: VisibleWhen | undefined,
  drivers: DriverIndex,
  answers: SectionAnswers,
): boolean {
  if (!condition) return true;
  const driver = drivers.get(condition.field_id);
  // Fail OPEN — see the block comment above.
  if (!driver || driver.retired) return true;
  const answer = get(answers[driver.section_id] ?? {}, driver.id);
  const chosen = Array.isArray(answer) ? answer : [answer];
  const values = condition.values ?? [];
  return chosen.some((v) => typeof v === 'string' && values.includes(v));
}

/**
 * Index a version's LIVE fields by `${section_id}.${id}` — the wizard's
 * replacement for `findFieldSpec`.
 *
 * Retired fields are excluded: they render nowhere on a family surface. Staff
 * surfaces that must still show a stored answer for a retired question use
 * `retiredFieldIndex` instead, so "renders nowhere" never becomes "the answer
 * disappeared".
 */
export function fieldIndex(version: FormDefinitionVersion | null): Map<string, FieldSpec> {
  const index = new Map<string, FieldSpec>();
  for (const field of version?.fields ?? []) {
    if (field.retired) continue;
    index.set(`${field.section_id}.${field.id}`, toFieldSpec(field));
  }
  return index;
}

/** The retired half — stored answers staff still need to read. */
export function retiredFieldIndex(version: FormDefinitionVersion | null): Map<string, FieldSpec> {
  const index = new Map<string, FieldSpec>();
  for (const field of version?.fields ?? []) {
    if (!field.retired) continue;
    index.set(`${field.section_id}.${field.id}`, toFieldSpec(field));
  }
  return index;
}

/**
 * A version's screens as the `ApplicationScreen`s the wizard already walks,
 * with any reference to a retired or unknown field dropped.
 *
 * Dropping is the correct behavior, not a silent loss: TRF retires a question
 * and the screen that referenced it simply stops showing it. A screen left with
 * no live fields at all is omitted rather than rendered empty — an empty step
 * with a Continue button reads as a broken form.
 */
export function toApplicationScreens(version: FormDefinitionVersion | null): ApplicationScreen[] {
  const live = fieldIndex(version);
  const screens: ApplicationScreen[] = [];
  for (const screen of [...(version?.screens ?? [])].sort((a, b) => a.order - b.order)) {
    const groups = screen.groups
      .map((group) => ({
        ...(group.label ? { label: group.label } : {}),
        fields: group.field_ids
          .filter((ref) => live.has(`${ref.section_id}.${ref.id}`))
          .map((ref) => ({ section: ref.section_id as SectionKey, key: ref.id })),
      }))
      .filter((group) => group.fields.length > 0);
    if (!groups.length) continue;
    screens.push({ id: screen.id, label: screen.label, eyebrow: screen.eyebrow, title: screen.title, groups });
  }
  return screens;
}

/** Distinct storage sections a screen touches (advancing saves each of them). */
export function screenSections(screen: ApplicationScreen): SectionKey[] {
  const set = new Set<SectionKey>();
  screen.groups.forEach((g) => g.fields.forEach((f) => set.add(f.section)));
  return [...set];
}

/**
 * A version's fields grouped by storage section — the shape the STAFF surfaces
 * already consume as `SECTION_FIELDS[section]`.
 *
 * `retired: true` selects the other half, which is exactly what
 * `RETIRED_SECTION_FIELDS` was: questions that render nowhere on a family
 * surface but whose stored answers staff must still be able to read. Retiring a
 * question hides it from families; it must never hide their answer from the
 * people deciding on their application.
 */
export function sectionFieldsFrom(
  version: FormDefinitionVersion | null,
  { retired = false }: { retired?: boolean } = {},
): Record<string, FieldSpec[]> {
  const grouped: Record<string, FieldSpec[]> = {};
  const fields = (version?.fields ?? [])
    .filter((f) => Boolean(f.retired) === retired)
    .sort((a, b) => a.order - b.order);
  for (const field of fields) {
    (grouped[field.section_id] ??= []).push(toFieldSpec(field));
  }
  return grouped;
}

/** One flagging target: the stored path, the plain section title it sits
 *  under, and a label that no other target in the same list shares. */
export interface FlaggableField {
  section: string;
  sectionTitle: string;
  key: string;
  label: string;
}

/** A section id the version does not title, made readable ("grant_request" →
 *  "Grant request") — never shown raw to staff (#2520). */
function humanizeSectionId(id: string): string {
  const words = id.replace(/[_.-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

function sectionTitleFor(version: FormDefinitionVersion | null, sectionId: string): string {
  return version?.sections?.find((s) => s.id === sectionId)?.title?.trim() || humanizeSectionId(sectionId);
}

/**
 * #2520: label a set of fields so no two read alike. The application asks
 * "If other, please specify" four times; as bare chips they were identical.
 * A duplicate takes the question that REVEALS it (`visible_when`), which is
 * what a reviewer means by "the other for which question"; if that still
 * collides it takes the section title, and a last collision is numbered.
 * Labels already unique are left exactly as the form writes them.
 */
function labelFields(
  fields: DefinitionField[],
  version: FormDefinitionVersion | null,
): FlaggableField[] {
  const byId = new Map((version?.fields ?? []).map((f) => [f.id, f]));
  const entries = fields.map((f) => ({
    section: f.section_id,
    sectionTitle: sectionTitleFor(version, f.section_id),
    key: `${f.section_id}.${f.id}`,
    label: f.label,
    field: f,
  }));
  const collisions = () => {
    const count = new Map<string, number>();
    entries.forEach((e) => count.set(e.label, (count.get(e.label) ?? 0) + 1));
    return count;
  };
  let count = collisions();
  for (const e of entries) {
    if ((count.get(e.label) ?? 0) < 2) continue;
    const driverId = e.field.visible_when?.field_id;
    const driver = driverId ? byId.get(driverId) : undefined;
    if (driver && driver.id !== e.field.id && driver.label.trim()) e.label = `${e.field.label} — ${driver.label.trim()}`;
  }
  count = collisions();
  for (const e of entries) {
    if ((count.get(e.label) ?? 0) >= 2) e.label = `${e.label} — ${e.sectionTitle}`;
  }
  count = collisions();
  const seen = new Map<string, number>();
  for (const e of entries) {
    if ((count.get(e.label) ?? 0) < 2) continue;
    const n = (seen.get(e.label) ?? 0) + 1;
    seen.set(e.label, n);
    e.label = `${e.label} (${n})`;
  }
  return entries.map(({ section, sectionTitle, key, label }) => ({ section, sectionTitle, key, label }));
}

/**
 * Every live field as a flagging target, in `section.key` form — the decision
 * dialog's picker. Grouped by the caller under `sectionTitle`; the label is
 * the question alone, disambiguated where two read alike (#2520).
 */
export function flaggableFields(version: FormDefinitionVersion | null): FlaggableField[] {
  const live = (version?.fields ?? [])
    .filter((f) => !f.retired)
    .sort((a, b) => a.order - b.order);
  return labelFields(live, version);
}

/**
 * #2520: the flagged paths of a needs-more-info request, labelled the way the
 * picker labelled them, in the order they were flagged. A question retired
 * since it was flagged still has its words; a path the version does not know
 * at all is shown as the path rather than dropped — staff must see every item
 * the family was asked about.
 */
export function flaggedFieldLabels(
  paths: string[],
  version: FormDefinitionVersion | null,
): Array<{ key: string; sectionTitle: string; label: string }> {
  const live = flaggableFields(version);
  const retired = labelFields(
    (version?.fields ?? []).filter((f) => f.retired).sort((a, b) => a.order - b.order),
    version,
  );
  const byKey = new Map([...retired, ...live].map((f) => [f.key, f]));
  return paths.map((path) => {
    const hit = byKey.get(path);
    if (hit) return { key: path, sectionTitle: hit.sectionTitle, label: hit.label };
    const sectionId = path.split('.')[0] ?? path;
    return { key: path, sectionTitle: sectionTitleFor(version, sectionId), label: path };
  });
}

/**
 * Flagged fields that a family can no longer SEE — retired (or absent) in the
 * currently published version even though the stamped version still carries
 * them.
 *
 * Staff flag a field to ask the family to fix it. If TRF has since retired that
 * question, the request is undeliverable: the family opens their application
 * and the question they were asked about is not there. Warning is the whole
 * point — silently flagging it would strand them.
 */
export function unaskableFlaggedFields(
  flagged: string[],
  published: FormDefinitionVersion | null,
): string[] {
  if (!published) return [];
  const live = new Set(
    (published.fields ?? []).filter((f) => !f.retired).map((f) => `${f.section_id}.${f.id}`),
  );
  return flagged.filter((path) => !live.has(path));
}

// ── pre-screen evaluation, from the definition instead of a hardcoded table ──

export interface PrescreenMiss {
  fieldId: string;
  line: string;
}

/**
 * Which of a family's pre-screen answers miss TRF's criteria, derived entirely
 * from the version's own per-option classification and numeric ranges (D-FB-2).
 *
 * This replaces `EligibilityCheck.evaluate`'s hardcoded chain. The seeded v1 was
 * generated BY running that chain, so day-one behavior is identical by
 * construction — and from here TRF can re-classify any answer without a deploy.
 *
 * #1639: an UNREVEALED question scores nothing, the same skip
 * `backend/src/lib/eligibility.js` `screenAnswers` already performs. A family
 * who answered a follow-up and then changed the driver above it left a stale
 * answer behind (hiding is never clearing, D-FB-11) — scoring it would issue a
 * stop screen the server, which never scores it, would not have issued.
 */
export function evaluateFromDefinition(
  version: FormDefinitionVersion | null,
  answers: Record<string, string>,
): PrescreenMiss[] {
  const misses: PrescreenMiss[] = [];
  const drivers = driverIndex(version?.fields);
  const bySection = prescreenAnswersBySection(version, answers);
  for (const field of version?.fields ?? []) {
    if (field.retired) continue;
    // Never asked, so never answered — whatever the answer object still holds.
    if (!isRevealed(field.visible_when, drivers, bySection)) continue;
    const answer = answers[field.id];
    if (answer === undefined || String(answer).trim() === '') continue;

    if (field.type === 'number') {
      const range = field.eligible_range;
      if (!range) continue; // an unranged numeric field is neutral by design
      const numeric = Number(answer);
      if (!Number.isFinite(numeric)) continue;
      const belowFloor = range.min != null && numeric < range.min;
      const aboveCeiling = range.max != null && numeric > range.max;
      if (belowFloor || aboveCeiling) misses.push({ fieldId: field.id, line: range.miss_line });
      continue;
    }

    const option = (field.options ?? []).find((o) => o.value === answer);
    if (option?.eligibility === 'miss') {
      misses.push({ fieldId: field.id, line: option.miss_line ?? '' });
    }
  }
  return misses;
}

/** Every live pre-screen field, in the order a family answers them. */
export function prescreenFields(version: FormDefinitionVersion | null): DefinitionField[] {
  return (version?.fields ?? [])
    .filter((f) => !f.retired)
    .sort((a, b) => a.order - b.order);
}

/**
 * Pre-screen answers in the shape `isRevealed` reads (#1639).
 *
 * The pre-screen holds its answers FLAT — `{ [field.id]: value }` — because it
 * runs before an application document exists, while the wizard holds one object
 * per storage section. Rather than give the pre-screen a second evaluator with
 * a second set of semantics (which is precisely how the preview and the wizard
 * came to disagree in the first place), this adapts the shape and the ONE
 * evaluator serves both. Field ids may carry a single dot level
 * (`assertMintableStorageKey`), so the value is written at its PATH — `get`
 * splits on dots and would otherwise miss a literal dotted key.
 */
export function prescreenAnswersBySection(
  version: FormDefinitionVersion | null,
  answers: Record<string, string>,
): SectionAnswers {
  const bySection: Record<string, Record<string, unknown>> = {};
  for (const field of version?.fields ?? []) {
    bySection[field.section_id] = setPath(
      bySection[field.section_id] ?? {},
      field.id,
      answers[field.id],
    );
  }
  return bySection;
}

/**
 * The live pre-screen questions this family is actually being ASKED (#1639).
 *
 * `prescreenFields` filters on `retired` alone, so before this the screen
 * rendered a hidden question, demanded it before "See if this is a fit", and
 * disagreed with the server about both: `collectScreen`
 * (`backend/src/routes/applications.js`) neither stores nor demands an
 * unrevealed answer, so the family was held on a question the server would
 * never have asked for.
 */
export function askedPrescreenFields(
  version: FormDefinitionVersion | null,
  answers: Record<string, string>,
): DefinitionField[] {
  const drivers = driverIndex(version?.fields);
  const bySection = prescreenAnswersBySection(version, answers);
  return prescreenFields(version).filter((f) => isRevealed(f.visible_when, drivers, bySection));
}

/** Live sections, in order — the pre-screen's question cards. */
export function liveSections(version: FormDefinitionVersion | null): DefinitionSection[] {
  return [...(version?.sections ?? [])].sort((a, b) => a.order - b.order);
}

/**
 * D-FB-3: with the continue-past-miss path retired, this is FALSE on the seeded
 * version and TRF can reverse the ruling from the builder without a deploy.
 * Defaults to false when the setting is absent — the safe direction is the hard
 * stop, never an accidental open door.
 */
export function allowsContinueDespiteMiss(version: FormDefinitionVersion | null): boolean {
  return version?.settings?.allow_continue_despite_miss === true;
}

/**
 * The words a family read for a stored answer (#1011 / AP-28).
 *
 * The wizard and the staff drawer render an option's LABEL; until #1011 the
 * staff detail page and the CSV rendered the stored VALUE, so `hospital_social_worker`
 * reached a human who never chose that phrasing — internal identity leaking to
 * a reader, the same class as #1098.
 *
 * A value with no matching option passes through UNCHANGED rather than blanking:
 * it happens for legacy answers and for options deleted before retire-not-delete
 * was enforced, and losing a family's answer is strictly worse than showing an
 * unfamiliar string. The backend CSV getter (`buildCsvColumns`) implements the
 * same rule on its side of the stack; `intake-option-parity.test.js` pins that
 * the two agree.
 *
 * TWO sources, in this order (B-431):
 *
 *  1. `optionLabels` — minted by `toFieldSpec` from a form DEFINITION, and the
 *     only one of the two that carries RETIRED options. It stays primary: the
 *     live `options` set no longer contains a retired value, so letting the
 *     fallback win would degrade exactly the oldest answers back to slugs.
 *  2. `options` — the fallback, for a HAND-WRITTEN `FieldSpec`. Code-owned
 *     specs (`GRANT_REQUEST_FIELDS`, `SECTION_FIELDS`) never pass through
 *     `toFieldSpec`, so they have `options` and no `optionLabels` at all.
 *     Reading only `optionLabels` made the staff detail page render intake
 *     answers as prose and the Grant request block as `rent_mortgage` — one
 *     page, two answers, two registers. The fallback covers every hand-written
 *     spec without asking each author to maintain a parallel map.
 */
export function optionLabelFor(
  field: Pick<FieldSpec, 'optionLabels' | 'options'>,
  value: string,
): string {
  return (
    field.optionLabels?.[value]
    ?? field.options?.find((o) => o.value === value)?.label
    ?? value
  );
}

/**
 * The option sets a staff EDITOR should offer (#1011 / D-CS-19).
 *
 * The staff drawer renders a family's intake against the version their
 * application was answered against — the stamped one — so staff read answers
 * beside the questions the family was actually asked. That is right for
 * READING and wrong for EDITING: an option TRF published last week is not
 * offered at all, which is what made AP-28's second acceptance criterion false
 * for every already-admitted family.
 *
 * So: the field set stays stamped (a question retired since is still shown, and
 * a question added since is not silently introduced into this family's record),
 * while each choice field's OPTIONS come from the published version. The
 * `optionLabels` maps are unioned, not replaced, so a value that was only ever
 * offered by the stamped version still renders as words.
 *
 * The other half of D-CS-19 — a stored value that is in NEITHER set — is
 * preserved at the point of render, so editing can never silently drop it.
 */
export function withPublishedOptions(
  specs: Record<string, FieldSpec[]>,
  published: FormDefinitionVersion | null,
): Record<string, FieldSpec[]> {
  if (!published) return specs;
  const byPath = new Map(
    (published.fields ?? []).map((f) => [`${f.section_id}.${f.id}`, f] as const),
  );
  const out: Record<string, FieldSpec[]> = {};
  for (const [sectionId, fields] of Object.entries(specs)) {
    out[sectionId] = fields.map((spec) => {
      const live = byPath.get(`${sectionId}.${spec.key}`);
      if (!live) return spec;
      const fresh = toFieldSpec(live);
      if (!fresh.options && !fresh.optionLabels) return spec;
      return {
        ...spec,
        options: fresh.options ?? spec.options,
        optionLabels: { ...(spec.optionLabels ?? {}), ...(fresh.optionLabels ?? {}) },
        conditionalOptions: fresh.conditionalOptions ?? spec.conditionalOptions,
      };
    });
  }
  return out;
}
